import { type FC, type Ref, useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { cn } from "../lib/utils";
import { ClickableIcon, Icon } from "../primitives";

export type SearchInputProps = {
	name?: string;
	defaultValue?: string;
	className?: string;
	inputClassName?: string;
	placeholder?: string;
	autofocus?: boolean;
	hideFocusStyles?: boolean;
	onChange?: (value: string) => void;
	testId?: string;
	ref?: Ref<HTMLInputElement>;
};

export const SearchInput: FC<SearchInputProps> = ({
	name,
	defaultValue,
	className,
	inputClassName,
	placeholder,
	autofocus,
	hideFocusStyles,
	onChange,
	testId,
	ref: forwardedRef,
}) => {
	const ref = useRef<HTMLInputElement | null>(null);
	const [value, setValue] = useState<string>(defaultValue ?? "");
	const [showClearButton, setShowClearButton] = useState<boolean>(value.length > 0);

	const assignInputRef = useCallback(
		(el: HTMLInputElement | null) => {
			ref.current = el;
			if (typeof forwardedRef === "function") forwardedRef(el);
			else if (forwardedRef) forwardedRef.current = el;
		},
		[forwardedRef],
	);

	const debounced = useDebouncedCallback((nextValue: string) => {
		onChange?.(nextValue);
	}, 450);

	useEffect(() => {
		if (value.length) {
			debounced.cancel();
			debounced(value);

			if (!showClearButton) {
				setShowClearButton(true);
			}
		} else {
			debounced.cancel();
			onChange?.(value);
			if (showClearButton) {
				setShowClearButton(false);
			}
		}
	}, [value]);

	useEffect(() => {
		return () => {
			ref.current = null;
		};
	}, []);

	return (
		<div
			className={cn(
				"flex items-center space-x-[10px] w-fit pr-[18px] pl-[15px] h-[36px] rounded-full bg-white cursor-pointer",
				!hideFocusStyles &&
					"disabled:cursor-not-allowed disabled:opacity-50 focus-within:outline-gray-400 focus-within:outline focus-within:outline-[1px] focus-within:outline-offset-[-1px] focus-within:shadow-[1px_1px_0_#d1d5db] focus-within:cursor-text",
				className,
			)}
			onClick={() => {
				if (ref.current) ref.current.focus();
			}}
		>
			<Icon type="magnifying-glass" size="mini" color="secondary" filled />
			<input
				data-testid={testId ?? "search-input"}
				data-autofocus={autofocus || undefined}
				name={name}
				type="text"
				ref={assignInputRef}
				value={value}
				placeholder={placeholder}
				onChange={(event) => {
					const newValue = event.target.value ?? "";
					if (newValue !== value) {
						setValue(event.target.value ?? "");
					}
				}}
				className={cn(
					"flex-1 h-full pr-[8px] pb-[1px] text-[14px] font-normal leading-[10px] text-primary cursor-pointer placeholder:text-gray-400 bg-transparent focus-visible:cursor-text focus:outline-none focus:placeholder-transparent placeholder:text-sm",
					inputClassName,
				)}
			/>
			{showClearButton && (
				<ClickableIcon
					onClick={(e) => {
						e?.stopPropagation();
						setValue("");
						if (ref.current) ref.current.blur();
					}}
					type="x-circle"
					size="mini"
					color="tertiary"
					filled
				/>
			)}
		</div>
	);
};
SearchInput.displayName = "SearchInput";
